import { CheckIcon, Copy02Icon } from "@/icons";
import { Button } from "@/kit/ui/button";
import { Input, InputDecorator } from "@/kit/ui/input";
import { toast } from "@/utils/toast";
import { DetailedHTMLProps, HTMLAttributes, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { cn } from "../utils/tailwind";
import { useClipboardAvailable } from "@/utils/hooks";

export interface CodeProps extends DetailedHTMLProps<
  HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> {
  content?: string;
}

export default function Code({ content, className, ...props }: CodeProps) {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  const clipboard = useClipboardAvailable();

  if (!content) {
    return null;
  }

  const onCopy = async (evt: React.MouseEvent) => {
    evt.preventDefault();
    await navigator.clipboard.writeText(content);
    setCopied(true);
    toast.success(
      intl.formatMessage({
        defaultMessage: "Code copied into clipboard!",
      }),
    );
  };

  return (
    <div {...props} className={cn(className, "w-full flex items-center gap-2")}>
      <Input
        className="w-full"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        aria-label={content}
        value={content}
        onFocus={(e) => e.target.select()}
        type="text"
        readOnly
        aria-readonly
        endDecorator={
          clipboard && (
            <InputDecorator className="p-0">
              <Button
                onClick={onCopy}
                size="md"
                variant="secondary"
                className="border-t-0 border-b-0 border-r-0 rounded-l-none"
              >
                {copied ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  <Copy02Icon className="h-4 w-4" />
                )}
                {copied ? (
                  <FormattedMessage defaultMessage="Copied!" />
                ) : (
                  <FormattedMessage defaultMessage="Copy" />
                )}
              </Button>
            </InputDecorator>
          )
        }
      />
    </div>
  );
}
